import { format, startOfWeek, endOfWeek, isWithinInterval, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const getWeeklyData = (activities, type) => {
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  
  return activities.filter(activity => 
    activity.type === type && 
    isWithinInterval(new Date(activity.date), { start: weekStart, end: weekEnd })
  );
};

export const calculateProgress = (current, target) => {
  return Math.min((current / target) * 100, 100);
};

// Funciones para calcular rangos de fechas
export const getDateRange = (filterType) => {
  const now = new Date();
  let start, end;

  switch (filterType) {
    case 'currentWeek':
      start = startOfWeek(now, { weekStartsOn: 1 });
      start.setHours(0, 0, 0, 0);
      end = endOfWeek(now, { weekStartsOn: 1 });
      end.setHours(23, 59, 59, 999);
      break;
    
    case 'lastWeek':
      const lastWeekDate = subWeeks(now, 1);
      start = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
      start.setHours(0, 0, 0, 0);
      end = endOfWeek(lastWeekDate, { weekStartsOn: 1 });
      end.setHours(23, 59, 59, 999);
      break;
    
    case 'currentMonth':
      start = startOfMonth(now);
      start.setHours(0, 0, 0, 0);
      end = endOfMonth(now);
      end.setHours(23, 59, 59, 999);
      break;
    
    case 'lastMonth':
      const lastMonthDate = subMonths(now, 1);
      start = startOfMonth(lastMonthDate);
      start.setHours(0, 0, 0, 0);
      end = endOfMonth(lastMonthDate);
      end.setHours(23, 59, 59, 999);
      break;
    
    default:
      // Por defecto, semana actual
      start = startOfWeek(now, { weekStartsOn: 1 });
      start.setHours(0, 0, 0, 0);
      end = endOfWeek(now, { weekStartsOn: 1 });
      end.setHours(23, 59, 59, 999);
  }

  return { start, end };
};

export const getFilterLabel = (filterType) => {
  const labels = {
    'currentWeek': 'Semana Actual',
    'lastWeek': 'Semana Pasada',
    'currentMonth': 'Mes Actual',
    'lastMonth': 'Mes Pasado'
  };
  return labels[filterType] || 'Semana Actual';
};

export const getStatusColor = (status) => {
  const colors = {
    'Cliente': 'bg-green-100 text-green-800 border-green-200',
    'Lead': 'bg-blue-100 text-blue-800 border-blue-200', 
    'Prospecto': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Potencial': 'bg-purple-100 text-purple-800 border-purple-200',
    'Inactivo': 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[status] || colors['Prospecto'];
};

/**
 * Convierte cualquier error del backend o de red en un mensaje claro para el usuario.
 * Evita que lleguen mensajes técnicos como [ErrorDetail(...)], stack traces, etc.
 *
 * @param {any} error - El error capturado en el catch
 * @param {string} fallback - Mensaje genérico si no se puede determinar la causa
 * @returns {string}
 */
// Devuelve la etiqueta legible para un campo de error, o '' si no debe mostrarse
// como prefijo (ej: errores generales que no pertenecen a un campo puntual).
const labelForField = (field) => {
  if (field === 'non_field_errors' || field === '__all__') return '';
  return FIELD_LABELS[field] || humanizeField(field);
};

/**
 * Intenta obtener el objeto de datos de error devuelto por el backend,
 * sin importar si el error viene con forma de Axios (error.response.data)
 * o de fetch (error.message con el body serializado como JSON, ver api.js).
 * Devuelve null si no se pudo determinar.
 */
const getBackendErrorData = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) return data;

  const message = error?.message;
  if (typeof message === 'string') {
    const trimmed = message.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch {
        // No era JSON válido, seguimos sin datos estructurados
      }
    }
  }
  return null;
};

// Extrae errores por campo de un objeto { field: [msg] } y devuelve un texto
// con "Etiqueta: mensaje" por cada campo, separado por saltos de línea.
const extractFieldErrors = (data) => {
  if (typeof data !== 'object' || Array.isArray(data) || !data) return null;
  const entries = Object.entries(data).filter(([, v]) => v);
  if (entries.length === 0) return null;
  const lines = entries.map(([field, msgs]) => {
    if (field === 'detail' || field === 'message' || field === 'type') return null; // manejados por separado
    const label = labelForField(field);
    const raw   = Array.isArray(msgs) ? msgs[0] : msgs;
    const msg   = cleanDjangoError(String(raw));
    return label ? `${label}: ${msg}` : msg;
  }).filter(Boolean);
  return lines.length > 0 ? lines.join('\n') : null;
};

export const parseErrorMessage = (error, fallback = 'Ocurrió un error inesperado. Por favor intentá de nuevo.') => {
  try {
    // Error de red / sin conexión
    if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
      return 'No se pudo conectar con el servidor. Verificá tu conexión a internet.';
    }

    // 1. Respuesta estructurada del backend (ej: con Axios o fetch con .response)
    const data = error?.response?.data;
    if (data) {
      const fieldMsg = extractFieldErrors(data);
      if (fieldMsg) return fieldMsg;
      if (typeof data.detail === 'string' && !data.detail.includes('ErrorDetail')) return data.detail;
      if (typeof data.message === 'string') return data.message;
      if (Array.isArray(data)) {
        const msg = cleanDjangoError(String(data[0]));
        if (msg) return msg;
      }
    }

    // 2. api.js convierte el error body a JSON.stringify → new Error(jsonString)
    //    Intentamos parsear el message como JSON y extraer campos
    if (error?.message) {
      const trimmed = error.message.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const fieldMsg = extractFieldErrors(parsed);
            if (fieldMsg) return fieldMsg;
            if (typeof parsed.detail === 'string') return cleanDjangoError(parsed.detail);
            if (typeof parsed.message === 'string') return parsed.message;
          }
          if (Array.isArray(parsed) && parsed.length > 0) {
            return cleanDjangoError(String(parsed[0]));
          }
        } catch {}
      }

      // 3. Mensaje JS genérico
      const cleaned = cleanDjangoError(trimmed);
      if (cleaned && cleaned !== trimmed) return cleaned;
      if (!trimmed.includes('ErrorDetail') && !trimmed.includes('at ') && !trimmed.startsWith('{')) {
        return trimmed;
      }
    }
  } catch (_) {}

  return fallback;
};

// Convierte nombres de campo snake_case a palabras legibles
const humanizeField = (field) => {
  return field
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Traducciones de campos del backend al español
const FIELD_LABELS = {
  // Genéricos
  name: 'Nombre',
  email: 'Correo electrónico',
  phone: 'Teléfono',
  contact_id: 'Contacto',
  company_id: 'Empresa',
  product_id: 'Producto',
  quantity: 'Cantidad',
  price_fob_unit: 'Precio FOB',
  total_amount: 'Total',
  status: 'Estado',
  stage: 'Etapa',
  items: 'Ítems',
  non_field_errors: '',
  detail: '',
  password: 'Contraseña',
  username: 'Usuario',
  stock: 'Stock',
  category: 'Categoría',
  brand: 'Marca',
  // Productos
  product_code: 'Código de producto',
  description: 'Descripción',
  category_id: 'Categoría',
  brand_id: 'Marca',
  product_line_id: 'Línea de producto',
  tag_ids: 'Etiquetas',
  brand_type: 'Tipo de marca',
  is_for_sale: 'Para venta',
  is_purchasable: 'Para compra',
  unit_price: 'Precio unitario',
  price_currency: 'Moneda de precio',
  cost: 'Costo',
  cost_currency: 'Moneda de costo',
  profit_margin: 'Margen de ganancia',
  cost_percentage: 'Costo %',
  incoterm: 'Incoterm',
  cost_rmb: 'Costo RMB',
  cost_fob: 'Costo FOB',
  price_fob: 'Precio FOB',
  pcs_per_ctn: 'PCS por CTN',
  cbm_per_ctn: 'CBM por CTN',
  factory_port: 'Puerto de fábrica',
  shipping_port: 'Puerto de embarque',
  unit: 'Unidad',
  is_service: 'Tipo (servicio/producto)',
  is_active: 'Estado activo',
  assortment: 'Surtido',
  pieces: 'Piezas',
  material_composition: 'Composición de material',
  size: 'Tamaño',
  packaging_type_id: 'Tipo de empaque',
  uses_batteries: 'Electronic',
  batteries_included: 'Pilas incluidas',
  recommended_age: 'Edad recomendada',
  battery_type_id: 'Tipo de batería',
  moq: 'MOQ',
  moq_oem: 'MOQ OEM',
  discount_fob: 'Descuento FOB',
  product_variant: 'Variante del producto',
  battery_info: 'Info de batería',
  product_height: 'Alto del producto',
  product_length: 'Largo del producto',
  product_width: 'Ancho del producto',
  ean: 'EAN',
  dun: 'DUN',
  master_pack_height: 'Alto del master pack',
  master_pack_length: 'Largo del master pack',
  master_pack_width: 'Ancho del master pack',
  master_pack_gross_weight: 'Peso bruto del master pack',
  master_pack_net_weight: 'Peso neto del master pack',
  units_per_master: 'Unidades por master',
  original_factory_cost: 'Costo original de fábrica',
  best_cost_with_order: 'Mejor costo con pedido',
  product_status: 'Fase del producto',
  accessories: 'Accesorios',
  added_date: 'Fecha de alta',
  inner_ctn: 'Inner CTN',
  factory_id: 'Fábrica',
  agent_id: 'Agente',
  manufacturer_code: 'Código de fabricante',
  main_image: 'Imagen principal',
  gallery_images: 'Imágenes de galería',
  video_url: 'URL de video',
  product_improvements: 'Mejoras del producto',
  ecommerce_description: 'Descripción ecommerce',
  // Contactos — General
  fantasy_name: 'Nombre fantasía',
  contact_type: 'Tipo de contacto',
  position: 'Cargo / Posición',
  area: 'Área',
  mobile: 'Móvil',
  postal_code: 'Código postal',
  comprador: 'Comprador',
  codigo_contacto: 'Código de contacto',
  address: 'Dirección',
  city: 'Ciudad',
  state_province: 'Provincia / Estado',
  country: 'País',
  categories: 'Categorías',
  wechat_id: 'ID de WeChat',
  website: 'Sitio WEB',
  description: 'Descripción',
  relationship_start_date: 'Fecha inicio relación',
  label_type: 'Tipo de etiqueta',
  comments: 'Comentarios',
  // Contactos — Financiero
  payment_term: 'Términos de pago',
  beneficiary_account_name: 'Cuenta beneficiaria',
  commission_percentage: 'Comisión (%)',
  supplier_currencies: 'Divisas proveedor',
  beneficiary_bank_address: 'Dirección banco beneficiario',
  swift_bic: 'SWIFT / BIC',
  routing_aba: 'Routing ABA',
  receipt_reminder: 'Recordatorio de recibo',
  tax_information: 'Información fiscal',
  discount_percentage: 'Descuento (%)',
  discount_list: 'Descuento',
  kam: 'KAM',
  sales_team: 'Equipo de ventas',
  price_list: 'Lista de precio',
  reference: 'Referencia',
  sector: 'Sector',
  id_type: 'Tipo de ID',
  id_number: 'Tax ID',
  bank: 'Banco',
  account_number: 'Número de cuenta',
  // Contactos — Info envío
  notify_party: 'Notify Party',
  consignee: 'Consignee',
  transit_id: 'ID del transitario',
  destination_port_text: 'Puerto destino',
  requirements: 'Requisitos',
  shipping_instructions: 'Instrucciones de envío',
  load_port_id: 'Puerto de carga',
  destination_port_ids: 'Puertos de destino',
  factory_port_id: 'Puerto de fábrica',
  // Contactos — Otra información
  store_number: 'Número de tienda',
  store_format: 'Formato de tienda',
  fairs: 'Ferias',
  // Pedidos / Cotizaciones
  order_date: 'Fecha de pedido',
  shipping_date: 'Fecha de envío',
  payment_terms: 'Condiciones de pago',
  currency: 'Moneda',
  rebate: 'Rebate',
};

// Limpia mensajes técnicos de Django REST Framework
const cleanDjangoError = (msg) => {
  if (!msg) return '';
  // [ErrorDetail(string='...', code='...')] → extraer solo el string
  const match = msg.match(/ErrorDetail\(string='([^']+)'/);
  if (match) return translateDjangoMsg(match[1]);
  // Si tiene múltiples ErrorDetail
  const allMatches = [...msg.matchAll(/ErrorDetail\(string='([^']+)'/g)];
  if (allMatches.length > 1) return allMatches.map(m => translateDjangoMsg(m[1])).join('. ');
  return translateDjangoMsg(msg);
};

const DJANGO_TRANSLATIONS = {
  'This field may not be null.': 'Este campo es obligatorio.',
  'This field may not be blank.': 'Este campo no puede estar vacío.',
  'This field is required.': 'Este campo es obligatorio.',
  'Enter a valid email address.': 'Ingresá un email válido.',
  'Ensure this value is greater than or equal to 0.': 'El valor debe ser mayor o igual a 0.',
  'A valid integer is required.': 'Ingresá un número entero válido.',
  'A valid number is required.': 'Ingresá un número válido.',
  'Este campo no puede ser nulo.': 'Este campo es obligatorio.',
  'Este campo no puede estar en blanco.': 'Este campo no puede estar vacío.',
  'Este campo es requerido.': 'Este campo es obligatorio.',
};

const translateDjangoMsg = (msg) => {
  if (!msg) return '';
  return DJANGO_TRANSLATIONS[msg.trim()] || msg;
};

/**
 * Parsea la respuesta de error del backend y devuelve un objeto
 * { fieldName: 'mensaje de error traducido' } para mostrar errores inline.
 * Los campos sin label conocido también son incluidos con su clave técnica.
 */
export const parseFieldErrors = (error) => {
  const data = getBackendErrorData(error);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};

  const errors = {};
  for (const [field, msgs] of Object.entries(data)) {
    if (field === 'detail' || field === 'message' || field === 'type') continue;
    if (!msgs) continue;
    const raw = Array.isArray(msgs) ? msgs[0] : msgs;
    const label = labelForField(field) || field;
    const message = cleanDjangoError(String(raw));
    errors[field] = { label, message };
  }
  return errors;
};